import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Generic placeholder for internal Player Hub sub-pages.
 * Each route below uses this same component with custom title/description until
 * the dedicated content is built out.
 */
const PAGES = {
  "coach-portal": { title: "College Coach Portal", subtitle: "Direct communication with 6,000+ college coaches", note: "Coach search, messaging, and recruiting outreach tools." },
  "insights": { title: "College Baseball Insights", subtitle: "Data-driven recruiting trends & analytics", note: "Aggregated metrics, conference trends, and pipeline data." },
  "recruiting-guide": { title: "85-Page College Recruiting Guide", subtitle: "Complete playbook for college baseball recruiting", note: "Download or read online — the definitive NSB guide." },
  "ncaa-rules": { title: "NCAA Recruiting Rules & Guidelines", subtitle: "Compliance simplified for athletes & families", note: "Current rules across D1, D2, D3, NAIA, JUCO." },
  "premium-videos": { title: "Premium Development Videos", subtitle: "Player information video library", note: "Position-by-position skills, mental game, recruiting tips." },
  "weekly-zoom": { title: "Weekly Recruiting Zoom Calls", subtitle: "Discounted group recruiting support", note: "Live Q&A every week with NSB recruiting experts." },
  "live-zoom": { title: "Live Zoom Group Training", subtitle: "Group skill development & mental performance sessions", note: "Schedule of upcoming live training Zooms." },
  "youth-potential": { title: "Youth Potential & Neuro-Change", subtitle: "Mindset & cognitive performance course", note: "Self-paced video curriculum on athletic mindset." },
  "mental-health": { title: "Mental Health Support", subtitle: "Athlete wellness & emotional performance", note: "Curated resources & confidential check-in options." },
  "market-u": { title: "Market U — Brand Development", subtitle: "Personal brand, social media & NIL strategy", note: "How to build a brand college coaches and sponsors notice." },
  "nsb-online": { title: "NSB Online", subtitle: "Digital training library", note: "On-demand training across hitting, pitching, fielding, mental performance." },
};

export default function PlayerHubStubPage() {
  const location = useLocation();
  const slug = (location.pathname.split("/").filter(Boolean)[0] || "").toLowerCase();
  const data = PAGES[slug] || { title: "Coming Soon", subtitle: "This section is launching shortly.", note: "" };
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft size={14} className="mr-1.5" /> Back to Player Dashboard
        </Link>

        <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-12 shadow-sm" data-testid={`stub-page-${slug}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] uppercase tracking-wider font-bold mb-4">
            <Sparkles size={12} /> Coming soon
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight" data-testid="stub-title">{data.title}</h1>
          <p className="text-base text-gray-600 mt-3" data-testid="stub-subtitle">{data.subtitle}</p>
          {data.note && (
            <div className="mt-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-sm text-gray-700">{data.note}</p>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/dashboard">
              <Button className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="return-dashboard-btn">Return to Dashboard</Button>
            </Link>
            <a href="mailto:rod@scoutseyeapp.com?subject=Player Hub Inquiry">
              <Button variant="outline" data-testid="contact-nsb-btn">
                Contact NSB <ExternalLink size={14} className="ml-1.5" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
